import {
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapCategory,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { keyBy, orderBy } from 'lodash';
import { YStack } from 'smoothie';

import { getNumAccountsForCategory } from '../utils/moneyMapUtils';
import { MoneyMapAccountRule, type RuleVariant } from './MoneyMapAccountRule';
import { MoneyMapCategoryHeader } from './MoneyMapCategoryHeader';

interface MoneyMapCategorySectionProps {
    category: MoneyMapCategory;
    moneyMap: MoneyMapWithRulesAndAccounts;
    accounts: MoneyMapAccountWithAccountDetails[];
    totalAmountCents: number | null;
    rules: MoneyMapRuleWithAccountDetails[];
    ruleVariant?: RuleVariant;
}

export const MoneyMapCategorySection = ({
    category,
    moneyMap,
    accounts,
    totalAmountCents,
    rules,
    ruleVariant,
}: MoneyMapCategorySectionProps) => {
    const accountsById = keyBy(accounts, 'id');

    const numAccounts = getNumAccountsForCategory(
        category,
        undefined, // Income account is not relevant to member app UI
        rules,
    );

    return (
        <YStack gap={20} marginBottom={16}>
            <MoneyMapCategoryHeader
                category={category}
                numAccounts={numAccounts}
                totalAmountCents={totalAmountCents}
            />
            <YStack gap={20} marginHorizontal={12}>
                {orderBy(rules, [
                    (rule) => {
                        if (rule.moneyMapRuleGroupId == null)
                            return rule.priority;
                        return (
                            moneyMap.moneyMapRuleGroups.find(
                                (g) => g.id === rule.moneyMapRuleGroupId,
                            )?.priority ?? rule.priority
                        );
                    },
                    'priority',
                ]).map((rule, index) => {
                    return (
                        <MoneyMapAccountRule
                            key={index}
                            category={category}
                            rule={rule}
                            moneyMap={moneyMap}
                            accountsById={accountsById}
                            showIncomeAccountBadge={
                                rule.moneyMapAccountId ===
                                moneyMap.incomeMoneyMapAccountId
                            }
                            variant={ruleVariant}
                        />
                    );
                })}
            </YStack>
        </YStack>
    );
};
